/**
 * Minimum Order Gate - Trade Theme
 * 
 * Prevents checkout when cart total is under $200.
 * Gates both cart drawer AND cart page.
 * 
 * INSTALLATION:
 * 1. Shopify Admin → Online Store → Themes → Edit code
 * 2. Under "Assets", click "Add a new asset" → Create blank file → "min-order-gate.js"
 * 3. Paste this script and Save
 * 4. Open layout/theme.liquid
 * 5. Add this right before </body>:
 *    <script src="{{ 'min-order-gate.js' | asset_url }}" defer></script>
 * 6. Save and test
 * 
 * NOTE: Do NOT install inside snippets/cart-drawer.liquid — load once globally.
 */

(function () {
  'use strict';

  // ===================
  // CONFIG
  // ===================
  const MIN_CENTS = 20000; // $200.00

  // Drawer selectors (confirmed for Trade theme)
  const DRAWER_ROOT_SELECTOR = '#CartDrawer';
  const DRAWER_FORM_SELECTOR = '#CartDrawer-Form';
  const DRAWER_CHECKOUT_BTN_SELECTOR = '#CartDrawer-Checkout';
  const DRAWER_CTAS_SELECTOR = '#CartDrawer .cart__ctas';

  // Cart page selectors (Trade/Dawn patterns)
  // Exclude drawer form to avoid double-matching
  const CART_PAGE_FORM_SELECTOR = 'form[action^="/cart"]:not(#CartDrawer-Form)';
  const CART_PAGE_CHECKOUT_BTN_SELECTORS = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'button[type="submit"][name="checkout"]',
    'input[type="submit"][name="checkout"]'
  ];
  const CART_PAGE_MESSAGE_INSERT_SELECTORS = [
    '.cart__footer',
    '#main-cart-footer',
    '.cart__ctas',
    'form[action^="/cart"]'
  ];

  // Message IDs
  const MSG_ID_DRAWER = 'min-order-msg-drawer';
  const MSG_ID_CART = 'min-order-msg-cart';

  // Debounce timing
  const DEBOUNCE_MS = 200;

  // ===================
  // LANGUAGE DETECTION
  // ===================
  function isSpanish() {
    // Check Shopify locale, html lang, or URL
    const htmlLang = document.documentElement.lang || '';
    const shopifyLocale = window.Shopify && window.Shopify.locale;
    const urlHasEs = window.location.pathname.startsWith('/es');
    
    return htmlLang.startsWith('es') || shopifyLocale === 'es' || urlHasEs;
  }

  function getMessage(key, values) {
    const messages = {
      en: {
        verifying: 'Verifying cart total...',
        minimum: 'Minimum order:',
        addMore: 'Add {amount} more to checkout.',
        fetchError: "Couldn't verify cart total. Please refresh the page."
      },
      es: {
        verifying: 'Verificando total del carrito...',
        minimum: 'Pedido mínimo:',
        addMore: 'Agregue {amount} más para continuar.',
        fetchError: 'No se pudo verificar el total. Por favor actualice la página.'
      }
    };
    
    const lang = isSpanish() ? 'es' : 'en';
    let msg = messages[lang][key] || messages['en'][key];
    
    if (values) {
      Object.keys(values).forEach(function(k) {
        msg = msg.replace('{' + k + '}', values[k]);
      });
    }
    
    return msg;
  }

  // ===================
  // STATE
  // ===================
  // Pessimistic: start as 'unknown' (blocked) until we verify cart total
  // States: 'unknown' | 'under' | 'ok'
  let gateState = 'unknown';
  let lastTotal = null;
  let isUpdating = false;
  let pendingTimer = null;
  // Pre-discount subtotal (sum of original_line_price). This is the tier basis, matching the
  // storefront black bar + the product-card tier table, so the cart progress bar always agrees.
  let origSubtotal = 0;

  // ===================
  // MONEY FORMAT
  // ===================
  function formatMoneyCents(cents) {
    // Prefer Shopify formatMoney if present
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        const fmt =
          (window.theme && window.theme.moneyFormat) ||
          (window.Shopify && window.Shopify.money_format) ||
          '${{amount}}';
        return window.Shopify.formatMoney(cents, fmt);
      }
    } catch (_) {}

    // Fallback
    return '$' + (cents / 100).toFixed(2);
  }

  // ===================
  // CART FETCH
  // ===================
  async function fetchCartTotal() {
    const res = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    });
    if (!res.ok) throw new Error('Failed to fetch /cart.js');
    const cart = await res.json();
    origSubtotal = (cart.items || []).reduce(function (s, it) {
      return s + (it.original_line_price || 0);
    }, 0);
    return Number(cart.total_price || 0);
  }

  // ===================
  // DOM HELPERS
  // ===================
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function ensureMessage(id, insertIntoEl) {
    let el = document.getElementById(id);
    if (el) return el;

    el = document.createElement('div');
    el.id = id;
    el.setAttribute('role', 'alert');
    el.style.display = 'none';
    el.style.width = '100%';
    el.style.boxSizing = 'border-box';
    el.style.margin = '0 0 12px 0';
    el.style.padding = '12px 14px';
    el.style.background = '#dc2626';
    el.style.color = '#ffffff';
    el.style.fontSize = '14px';
    el.style.fontWeight = '600';
    el.style.borderRadius = '6px';
    el.style.textAlign = 'center';
    el.style.lineHeight = '1.35';

    if (insertIntoEl) {
      // Insert as the first child, before any flex/grid items
      insertIntoEl.insertBefore(el, insertIntoEl.firstChild);
      // Force full width in flex containers
      insertIntoEl.style.flexWrap = 'wrap';
    } else {
      // Fallback: try cart form, then body
      const fallback = qs(CART_PAGE_FORM_SELECTOR) || qs('#CartDrawer-Form') || document.body;
      fallback.insertBefore(el, fallback.firstChild);
    }
    return el;
  }

  function setButtonGated(btn, gated) {
    if (!btn) return;
    btn.disabled = !!gated;
    btn.style.opacity = gated ? '0.55' : '';
    btn.style.cursor = gated ? 'not-allowed' : '';
    btn.setAttribute('aria-disabled', gated ? 'true' : 'false');
  }

  function findCartPageCheckoutButtons() {
    // Search the whole document, not just inside the cart <form>. Dawn's cart-page checkout
    // button sits OUTSIDE the form (it references it via a form="" attribute), so a form-scoped
    // search misses it entirely. Exclude the drawer button (gated separately above).
    return qsa('button[name="checkout"], input[name="checkout"]').filter(function (b) {
      return b.id !== 'CartDrawer-Checkout' && !(b.closest && b.closest('#CartDrawer'));
    });
  }

  function getCartPageMessageContainer() {
    for (const sel of CART_PAGE_MESSAGE_INSERT_SELECTORS) {
      const el = qs(sel);
      if (el) return el;
    }
    return null;
  }

  // ===================
  // TIER PROGRESS BAR (subtotal >= minimum)
  // ===================
  // Once the order clears the minimum, the "wholesale minimum" note is replaced by a thick
  // progress bar showing how close the order is to the NEXT volume-discount tier. Tiers + minimum
  // come from the storefront bar's data-* attrs (the Fusion-synced onarys/volume_tiers metafield),
  // so this mirrors the black bar exactly. Rendered in both the drawer and the cart page.
  let progressStylesInjected = false;
  function injectProgressStyles() {
    if (progressStylesInjected) return;
    progressStylesInjected = true;
    const css =
      '.mog-tier-progress{width:100%;box-sizing:border-box;margin:20px 0 14px;}' +
      '.mog-tier-progress__track{height:16px;background:#e6e6e6;border-radius:999px;overflow:hidden;}' +
      // Two-class selector (0,2,0) so it beats Dawn's `div:empty{display:none}` (0,1,1) — the fill
      // is an empty div and would otherwise be hidden by that base rule.
      '.mog-tier-progress .mog-tier-progress__fill{display:block;height:100%;width:0;background:rgb(255,223,48);border-radius:999px;transition:width .35s ease;}' +
      '.mog-tier-progress__msg{margin-top:8px;font-size:14px;font-weight:600;line-height:1.35;text-align:center;color:#1a1a1a;}' +
      // "Reached a level" states — congrats (just crossed a tier) + max (top tier): a full green bar
      // in a light-green celebratory box.
      '.mog-tier-progress--congrats,.mog-tier-progress--max{background:#edf7ed;border:1px solid #cdeccd;border-radius:10px;padding:12px 14px;}' +
      '.mog-tier-progress--congrats .mog-tier-progress__fill,.mog-tier-progress--max .mog-tier-progress__fill{background:#2e8b2e;}' +
      '.mog-tier-progress--congrats .mog-tier-progress__msg,.mog-tier-progress--max .mog-tier-progress__msg{color:#137a37;font-weight:700;}';
    const style = document.createElement('style');
    style.id = 'mog-tier-progress-styles';
    style.textContent = css;
    document.head.appendChild(style);
  }

  // Tiers + minimum from the storefront bar (metafield-driven; dollars -> cents), with fallback.
  function getTierConfig() {
    const bar = qs('.upsell-bar');
    let tiers = [35000, 50000, 100000, 150000];
    let min = MIN_CENTS;
    if (bar) {
      const parsed = (bar.getAttribute('data-tiers') || '').split(',')
        .map(function (x) { return Math.round(parseFloat(x) * 100); })
        .filter(function (n) { return n > 0; });
      if (parsed.length) tiers = parsed;
      const m = Math.round(parseFloat(bar.getAttribute('data-minimum') || '') * 100);
      if (m) min = m;
    }
    tiers.sort(function (a, b) { return a - b; });
    return { tiers: tiers, min: min, top: tiers[tiers.length - 1] };
  }

  function progressMessage(amountToNextCents) {
    const amt = formatMoneyCents(amountToNextCents);
    return isSpanish()
      ? 'Agrega ' + amt + ' USD en productos para recibir el siguiente nivel de descuentos'
      : 'Add ' + amt + ' USD in products to receive the next level of discounts';
  }
  function maxTierMessage() {
    return isSpanish()
      ? '✓ Has desbloqueado el mayor descuento en tu pedido'
      : '✓ You’ve unlocked the biggest discount on your order';
  }
  function congratsMessage() {
    return isSpanish()
      ? '🎉 ¡Felicidades! Has alcanzado nuestro siguiente nivel de descuentos'
      : '🎉 Congratulations! You have reached our next level of discounts';
  }

  function ensureTierProgressEl(id, afterEl) {
    let el = document.getElementById(id);
    if (el) return el;
    el = document.createElement('div');
    el.id = id;
    el.className = 'mog-tier-progress';
    el.innerHTML =
      '<div class="mog-tier-progress__track"><div class="mog-tier-progress__fill"></div></div>' +
      '<div class="mog-tier-progress__msg"></div>';
    if (afterEl && afterEl.parentNode) {
      afterEl.parentNode.insertBefore(el, afterEl.nextSibling);
    }
    return el;
  }

  // taxNote = the "wholesale minimum" note for this surface (hidden once past the minimum, and
  // replaced by the progress bar). id = a stable id for this surface's bar.
  function renderTierProgress(taxNote, id) {
    if (!taxNote) return;
    const cfg = getTierConfig();
    const s = origSubtotal;
    const pastMin = gateState === 'ok' && s >= cfg.min;

    // Past the minimum, the "wholesale minimum" note is always hidden.
    taxNote.style.display = pastMin ? 'none' : '';

    const el = ensureTierProgressEl(id, taxNote);
    if (!pastMin) { el.style.display = 'none'; return; }

    const fill = el.querySelector('.mog-tier-progress__fill');
    const msg = el.querySelector('.mog-tier-progress__msg');
    // Reset the "reached a level" styling each render; the branches below re-add as needed.
    el.classList.remove('mog-tier-progress--max', 'mog-tier-progress--congrats');

    // Top tier reached — always show the "biggest discount" confirmation (there is no next tier).
    if (s >= cfg.top) {
      el.style.display = '';
      el.classList.add('mog-tier-progress--max');
      fill.style.width = '100%';
      msg.textContent = maxTierMessage();
      return;
    }

    // Bracket fill: within [lower, next), where lower is the last milestone crossed (the minimum
    // or a tier) and next is the first tier above the subtotal.
    const milestones = [cfg.min].concat(cfg.tiers.filter(function (t) { return t > cfg.min; }));
    let lower = cfg.min;
    for (let i = 0; i < milestones.length; i++) { if (milestones[i] <= s) lower = milestones[i]; }
    let next = cfg.top;
    for (let i = 0; i < cfg.tiers.length; i++) { if (cfg.tiers[i] > s) { next = cfg.tiers[i]; break; } }

    const span = next - lower;
    const pct = span > 0 ? Math.max(0, Math.min(100, ((s - lower) / span) * 100)) : 0;

    // Just crossed a discount tier — celebrate for the first 30% of the new bracket with a full
    // green bar. Only when `lower` is an actual tier (not the $200 minimum), since clearing the
    // minimum doesn't unlock a discount.
    if (lower > cfg.min && pct <= 30) {
      el.style.display = '';
      el.classList.add('mog-tier-progress--congrats');
      fill.style.width = '100%';
      msg.textContent = congratsMessage();
      return;
    }

    // Only surface the upsell once the order is at least halfway to the next tier — i.e. the
    // remaining gap is 50% or less of the tier-to-tier span. If more than 50% is still needed,
    // hide the bar + message entirely and leave just the checkout button.
    if (pct < 50) { el.style.display = 'none'; return; }

    el.style.display = '';
    fill.style.width = pct + '%';
    msg.textContent = progressMessage(next - s);
  }

  // ===================
  // APPLY GATE TO UI
  // ===================
  function applyGateToUI() {
    const isBlocked = gateState !== 'ok';
    const isUnknown = gateState === 'unknown';
    const needed = lastTotal !== null ? Math.max(0, MIN_CENTS - lastTotal) : MIN_CENTS;

    // Drawer
    const drawerBtn = qs(DRAWER_CHECKOUT_BTN_SELECTOR);
    if (drawerBtn) {
      const ctas = qs(DRAWER_CTAS_SELECTOR) || drawerBtn.closest('.cart__ctas') || drawerBtn.parentElement;
      const msg = ensureMessage(MSG_ID_DRAWER, ctas);
      setButtonGated(drawerBtn, isBlocked);

      if (isUnknown) {
        msg.innerHTML = '<div>' + getMessage('verifying') + '</div>';
        msg.style.display = 'block';
      } else if (gateState === 'under') {
        msg.innerHTML =
          '<div>' + getMessage('minimum') + ' <strong>' + formatMoneyCents(MIN_CENTS) + '</strong></div>' +
          '<div>' + getMessage('addMore', { amount: '<strong style="text-decoration: underline;">' + formatMoneyCents(needed) + '</strong>' }) + '</div>';
        msg.style.display = 'block';
      } else {
        msg.style.display = 'none';
      }
    }

    // Cart page
    const cartBtns = findCartPageCheckoutButtons();
    if (cartBtns.length) {
      // Put the banner right above the button (its own .cart__ctas), matching the drawer.
      const container = (cartBtns[0].closest && cartBtns[0].closest('.cart__ctas')) || getCartPageMessageContainer();
      const msg = ensureMessage(MSG_ID_CART, container);

      for (const b of cartBtns) setButtonGated(b, isBlocked);

      if (isUnknown) {
        msg.innerHTML = '<div>' + getMessage('verifying') + '</div>';
        msg.style.display = 'block';
      } else if (gateState === 'under') {
        msg.innerHTML =
          '<div>' + getMessage('minimum') + ' <strong>' + formatMoneyCents(MIN_CENTS) + '</strong></div>' +
          '<div>' + getMessage('addMore', { amount: '<strong style="text-decoration: underline;">' + formatMoneyCents(needed) + '</strong>' }) + '</div>';
        msg.style.display = 'block';
      } else {
        msg.style.display = 'none';
      }
    }

    // Tier progress bar — replaces the "wholesale minimum" note on both surfaces once past the
    // minimum (the drawer and the cart page each get their own bar).
    renderTierProgress(qs('#CartDrawer .tax-note'), 'mog-tier-progress-drawer');
    const cartTaxNote = qsa('.tax-note').filter(function (n) {
      return !(n.closest && n.closest('#CartDrawer'));
    })[0];
    renderTierProgress(cartTaxNote, 'mog-tier-progress-cart');
  }

  // ===================
  // MAIN UPDATE
  // ===================
  async function refreshGate(force) {
    if (isUpdating) return;
    isUpdating = true;

    try {
      const total = await fetchCartTotal();

      if (!force && total === lastTotal) {
        isUpdating = false;
        return;
      }

      lastTotal = total;
      gateState = total >= MIN_CENTS ? 'ok' : 'under';
      applyGateToUI();
    } catch (err) {
      console.error('[MinOrderGate]', err);
      // Fail closed: if we can't verify, block checkout
      gateState = 'unknown';
      lastTotal = null;
      applyGateToUI();
      // Show error message
      showFetchError();
    } finally {
      isUpdating = false;
    }
  }

  function showFetchError() {
    const errorMsg = getMessage('fetchError');
    const drawerBtn = qs(DRAWER_CHECKOUT_BTN_SELECTOR);
    if (drawerBtn) {
      const msg = document.getElementById(MSG_ID_DRAWER);
      if (msg) {
        msg.innerHTML = '<div>' + errorMsg + '</div>';
        msg.style.display = 'block';
      }
    }
    const cartMsg = document.getElementById(MSG_ID_CART);
    if (cartMsg) {
      cartMsg.innerHTML = '<div>' + errorMsg + '</div>';
      cartMsg.style.display = 'block';
    }
  }

  function debounceRefresh(force) {
    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(function () {
      refreshGate(!!force);
    }, DEBOUNCE_MS);
  }

  // ===================
  // HARD BLOCK AS BACKUP (submit/click capture)
  // ===================
  function blockIfUnderMin(e) {
    // Pessimistic: block if state is unknown OR under
    // Only allow if state is explicitly 'ok'
    if (gateState === 'ok') return;

    const t = e.target;

    // Submit events - only block checkout submissions, not add-to-cart or quantity updates
    if (e.type === 'submit') {
      // Check if this submit was triggered by a checkout button
      // e.submitter is the button/input that triggered the submit
      const submitter = e.submitter;
      const isCheckoutSubmit = submitter && 
        (submitter.name === 'checkout' || submitter.id === 'CartDrawer-Checkout');
      
      // Also check if it's the drawer form being submitted via checkout
      const form = t;
      const isDrawerForm = form && form.matches && form.matches(DRAWER_FORM_SELECTOR);
      
      // Only block if this is actually a checkout submission
      if (isCheckoutSubmit || (isDrawerForm && submitter && submitter.name === 'checkout')) {
        e.preventDefault();
        e.stopPropagation();
        // If unknown, trigger a refresh
        if (gateState === 'unknown') {
          refreshGate(true);
        } else {
          applyGateToUI();
        }
      }
      return;
    }

    // Click events
    if (e.type === 'click') {
      const checkoutEl = t && t.closest
        ? t.closest([
            DRAWER_CHECKOUT_BTN_SELECTOR,
            'button[name="checkout"]',
            'input[name="checkout"]'
          ].join(','))
        : null;

      if (checkoutEl) {
        e.preventDefault();
        e.stopPropagation();
        // If unknown, trigger a refresh
        if (gateState === 'unknown') {
          refreshGate(true);
        } else {
          applyGateToUI();
        }
      }
    }
  }

  // ===================
  // OBSERVE DRAWER ONLY (minimal)
  // ===================
  function setupDrawerObserver() {
    // Watch the drawer AND the cart-page sections. The cart page re-renders its footer/items on
    // quantity changes, which recreates the checkout button and wipes our banner — so we must
    // re-apply the gate whenever either region mutates.
    const roots = [
      qs(DRAWER_ROOT_SELECTOR),
      qs('#main-cart-footer'),
      qs('#main-cart-items')
    ].filter(Boolean);

    roots.forEach(function (root) {
      const obs = new MutationObserver(function () {
        debounceRefresh(false);
      });
      obs.observe(root, { childList: true, subtree: true });
    });
  }

  // ===================
  // INIT
  // ===================
  function init() {
    injectProgressStyles();

    // Immediately apply blocked state (pessimistic)
    applyGateToUI();
    
    // Then fetch actual cart total
    refreshGate(true);

    // Backup blockers
    document.addEventListener('submit', blockIfUnderMin, true);
    document.addEventListener('click', blockIfUnderMin, true);

    // Drawer observer
    setupDrawerObserver();

    // Re-check when returning to tab
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') debounceRefresh(true);
    });

    // Re-check on pageshow (handles bfcache)
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        gateState = 'unknown';
        debounceRefresh(true);
      }
    });

    // Re-check after Shopify theme cart updates (Dawn/Trade dispatches events)
    document.addEventListener('cart:refresh', function () {
      debounceRefresh(true);
    });

    console.log('[MinOrderGate] Loaded. Minimum:', formatMoneyCents(MIN_CENTS));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
